import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/utils.ts'

export interface RadioOption<TValue extends string = string> {
  value: TValue
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
}

export interface RadioGroupProps<TValue extends string = string> {
  name?: string
  value: TValue
  onValueChange: (value: TValue) => void
  options: RadioOption<TValue>[]
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  orientation?: 'horizontal' | 'vertical'
  className?: string
  disabled?: boolean
}

/**
 * Gruppe gegenseitig ausschliessender Optionen. Ersatz fuer nackte
 * `<input type="radio">` in Formularen.
 */
export function RadioGroup<TValue extends string = string>({
  name,
  value,
  onValueChange,
  options,
  label,
  hint,
  error,
  orientation = 'horizontal',
  className,
  disabled,
}: RadioGroupProps<TValue>) {
  const autoName = useId()
  const groupName = name ?? autoName
  return (
    <div
      className={cn('flex flex-col gap-1', className)}
      role="radiogroup"
      aria-label={typeof label === 'string' ? label : undefined}
    >
      {label ? <span className="text-small font-medium text-gray-700">{label}</span> : null}
      <div
        className={cn(
          'flex gap-3 flex-wrap',
          orientation === 'vertical' && 'flex-col gap-2',
        )}
      >
        {options.map((option) => {
          const checked = option.value === value
          const optionDisabled = disabled || option.disabled
          return (
            <label
              key={option.value}
              className={cn(
                'inline-flex items-start gap-2 cursor-pointer select-none text-body',
                optionDisabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <span className="relative inline-flex shrink-0 mt-0.5">
                <input
                  type="radio"
                  name={groupName}
                  value={option.value}
                  checked={checked}
                  disabled={optionDisabled}
                  onChange={() => onValueChange(option.value)}
                  className={cn(
                    'peer appearance-none h-4 w-4 rounded-full border border-gray-300 bg-white transition',
                    'checked:border-brand-700',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
                  )}
                />
                <span
                  className={cn(
                    'pointer-events-none absolute inset-0 flex items-center justify-center',
                    'opacity-0 peer-checked:opacity-100',
                  )}
                >
                  <span className="h-2 w-2 rounded-full bg-brand-700" />
                </span>
              </span>
              <span className="flex flex-col">
                <span className="text-body text-gray-900">{option.label}</span>
                {option.hint ? <span className="text-caption text-gray-500">{option.hint}</span> : null}
              </span>
            </label>
          )
        })}
      </div>
      {hint && !error ? <span className="text-caption text-gray-500">{hint}</span> : null}
      {error ? <span className="text-caption text-red-600">{error}</span> : null}
    </div>
  )
}
